/**
 * Customized this component to update the quantity input styles and basic validations
 */

import { numberInputContainerSX } from '@/components/blocks/NumberInput/styles/container';
import { quantityControlsSX } from '@/components/blocks/NumberInput/styles/customControls';
import {
	alterAdornmentControlSX,
	numberInputAlterAdornmentSX,
	numberInputInputAdornmentSX,
} from '@/components/blocks/NumberInput/styles/inputAdornment';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { useLocalization } from '@/data/Localization';
import { combineSX } from '@/utils/combineSX';
import { convertMaybeStringToInt } from '@/utils/convertMaybeStringToInt';
import { formatNumberValue } from '@/utils/formatNumberValue';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { ChangeEvent, FC, FocusEvent, useCallback, useEffect, useRef, useState } from 'react';

type NumberValue = number | null;
type MaybeString = string | number | null;

type Props = Omit<TextFieldProps, 'onChange' | 'value'> & {
	value: MaybeString;
	loading?: boolean;
	min?: number;
	max?: number;
	precision?: number;
	thousandSeparator?: string;
	decimalSeparator?: string;
	showControls?: boolean;
	altControls?: boolean;
	prefix?: string;
	postfix?: string;
	onChange: (value: NumberValue) => void;
	onBlur?: () => void;
	customValidator?: (value: NumberValue) => boolean;
	disallowEmptyOnBlur?: boolean;
	maxLength?: number;
	isControlled?: boolean; // indicates that the value is controlled by caller
	mini?: boolean;
};

const removeRestrictedCharacters = ({ string }: { string: string }) =>
	string.replace(/[^0-9]/g, '');

/**
 * Number input field for incrementing and decrementing numbers, with formatting.
 * @note This component does not support fractional min/max values.
 */
export const NumberInput: FC<Props> = ({
	onChange,
	loading = false,
	precision = 0,
	decimalSeparator = '.',
	thousandSeparator = ',',
	showControls = false,
	value,
	min = 0,
	max = 9999999,
	prefix,
	postfix,
	altControls = false,
	sx,
	disallowEmptyOnBlur,
	customValidator,
	maxLength = null,
	isControlled = false,
	mini = false,
	onBlur,
	...props
}) => {
	const common = useLocalization('Common');
	const [fieldValue, setFieldValue] = useState<MaybeString>(() =>
		formatNumberValue({ value, precision, decimalSeparator, thousandSeparator, min, max })
	);
	const [internalValue, setInternalValue] = useState<NumberValue>(() =>
		convertMaybeStringToInt({ input: value, decimalSeparator, thousandSeparator })
	);
	const [externalUpdate, setExternalUpdate] = useState<NumberValue>(() =>
		convertMaybeStringToInt({ input: value, decimalSeparator, thousandSeparator })
	);
	const { disabled } = props;

	const updateValue = useCallback(
		(raw: string, isFromStep: boolean) => {
			const cleaned = isFromStep
				? formatNumberValue({
						value: raw,
						precision,
						decimalSeparator,
						thousandSeparator,
						min,
						max,
				  })
				: removeRestrictedCharacters({ string: raw ?? '' });

			setFieldValue(cleaned);

			const intCandidate = convertMaybeStringToInt({
				input: cleaned,
				decimalSeparator,
				thousandSeparator,
			});
			setInternalValue(intCandidate);
		},
		[decimalSeparator, thousandSeparator, min, max, precision]
	);

	useEffect(() => {
		// Check if the value is "invalid" (0, or less than min, or more than max)
		const isInvalid =
			internalValue === 0 ||
			(internalValue !== null && (internalValue < min || internalValue > max));

		if (isInvalid) {
			const handler = setTimeout(() => {
				const correctedValue =
					internalValue !== null && internalValue > max ? max : min;
				const fallbackValue = correctedValue.toString();
				setFieldValue(
					formatNumberValue({
						value: fallbackValue,
						precision,
						decimalSeparator,
						thousandSeparator,
						min,
						max,
					})
				);
				setInternalValue(correctedValue);

				if (onChange) onChange(correctedValue);
			}, 2000); // 2 second delay (adjust as needed)

			return () => clearTimeout(handler); // Clear timeout if user types again
		}
	}, [internalValue, min, precision, decimalSeparator, thousandSeparator, onChange, max]);

	const changeHandler = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => updateValue(e.target.value, false),
		[updateValue]
	);

	const step = useCallback(
		(amount: number) => () => updateValue(((internalValue ?? 0) + amount).toString(), true),
		[internalValue, updateValue]
	);
	const inputRef = useRef<HTMLInputElement>();

	const onBlurHandler = useCallback(
		(e: FocusEvent<HTMLElement>) => {
			onBlur?.();
			const normalized = formatNumberValue({
				value: fieldValue,
				precision,
				decimalSeparator,
				thousandSeparator,
				min,
				max,
			});

			setFieldValue(normalized);

			const intNormalized = convertMaybeStringToInt({
				input: normalized,
				decimalSeparator,
				thousandSeparator,
			});
			setInternalValue(intNormalized);

			if (onChange) {
				onChange(intNormalized);
			}

			if (
				disallowEmptyOnBlur &&
				e.target instanceof HTMLInputElement &&
				e.target.value.trim() === ''
			) {
				const fallback = (min ?? '').toString();
				setFieldValue(fallback);
				const intFallback = convertMaybeStringToInt({
					input: fallback,
					decimalSeparator,
					thousandSeparator,
				});
				setInternalValue(intFallback);
				if (onChange) onChange(intFallback);
			}
		},
		[
			fieldValue,
			precision,
			decimalSeparator,
			thousandSeparator,
			min,
			max,
			disallowEmptyOnBlur,
			onChange,
			onBlur,
		]
	);

	/**
	 * Fire change event if real values have changed
	 */
	useEffect(() => {
		if (!onChange || externalUpdate === internalValue) {
			return;
		}
		onChange(internalValue);
		setExternalUpdate(internalValue);
		if (inputRef.current && customValidator) {
			inputRef.current.setCustomValidity(customValidator(internalValue) ? '' : 'error');
			// We currently aren't showing built-in form validation message.
			// This just needs to be a non-empty string for useForm() to trigger error state.
		}
	}, [onChange, internalValue, externalUpdate, customValidator]);

	/**
	 * Update based on received input changes.
	 */
	useEffect(() => {
		const updatedValue = convertMaybeStringToInt({
			input: value,
			decimalSeparator,
			thousandSeparator,
		});
		setInternalValue(updatedValue);
		setExternalUpdate(updatedValue);
		setFieldValue(value);
	}, [decimalSeparator, thousandSeparator, value]);

	return loading ? (
		<ProgressIndicator />
	) : (
		<TextField
			{...props}
			value={fieldValue ?? ''}
			type="text"
			sx={combineSX([numberInputContainerSX(showControls), sx])}
			onChange={changeHandler}
			inputProps={{ 'aria-label': common.quantity.t({ n: fieldValue ?? '' }), maxLength }}
			inputRef={inputRef}
			onBlur={onBlurHandler}
			InputProps={{
				startAdornment: showControls ? (
					<Button
						disabled={disabled}
						aria-label={common.decrement.t()}
						onClick={step(-1)}
						onBlur={onBlurHandler}
						sx={quantityControlsSX.controls(!!disabled, !!fieldValue && +fieldValue === +min)}
						id={!mini ? 'quantityDecrease' : undefined}
						className={mini ? 'quick-cart__quantity-decrease' : 'quantity-input-less'}
					>
						<RemoveIcon />
					</Button>
				) : prefix ? (
					<InputAdornment position="start" sx={numberInputInputAdornmentSX}>
						{prefix}
					</InputAdornment>
				) : null,
				endAdornment: showControls ? (
					<Button
						disabled={disabled}
						aria-label={common.increment.t()}
						onClick={step(1)}
						onBlur={onBlurHandler}
						sx={quantityControlsSX.controls(
							!!disabled,
							max ? !!fieldValue && +fieldValue === +max : false
						)}
						id={!mini ? 'quantityIncrease' : undefined}
						className={mini ? 'quick-cart__quantity-increase' : 'quantity-input-more'}
					>
						<AddIcon />
					</Button>
				) : postfix ? (
					<InputAdornment position="end" sx={numberInputInputAdornmentSX}>
						{postfix}
					</InputAdornment>
				) : altControls ? (
					<InputAdornment position="end" sx={numberInputAlterAdornmentSX}>
						<Box display={'flex'} flexDirection={'column'}>
							<IconButton
								size="small"
								aria-label={common.increment.t()}
								onMouseDown={(e) => e.preventDefault()}
								onClick={step(1)}
								tabIndex={1}
								sx={alterAdornmentControlSX}
							>
								<ArrowDropUpIcon fontSize="small" />
							</IconButton>

							<IconButton
								size="small"
								aria-label={common.decrement.t()}
								onMouseDown={(e) => e.preventDefault()}
								onClick={step(-1)}
								tabIndex={-1}
								sx={alterAdornmentControlSX}
							>
								<ArrowDropDownIcon fontSize="small" />
							</IconButton>
						</Box>
					</InputAdornment>
				) : null,
			}}
		/>
	);
};
